import type { StockShortageDto } from '@matix/api-client';
import { useStockLevels } from '../../store/stockLevels';
import { useLang } from '../../lib/language';
import { XIcon } from '../../components/icons';

/**
 * "Not enough stock" — the one dialog every shortage lands in, whether the
 * cart caught it before Charge or the server refused the sale.
 *
 * Bilingual by design: the title, the explanation and every column read in
 * the till's language, and each row names the item in that language too.
 * `blocking` says which of two things it is: a refusal (the tenant does not
 * sell below stock — the cashier must fix the cart) or a warning (the sale
 * may go ahead, but the shelf will read negative afterwards).
 *
 * Sits above whatever modal is open: it is not in the `openModal` slot, so
 * the payment modal underneath is left exactly as it was.
 */
export function StockShortageDialog(): JSX.Element | null {
  const dialog = useStockLevels((s) => s.dialog);
  const closeDialog = useStockLevels((s) => s.closeDialog);
  const { t, lang } = useLang();
  if (!dialog) return null;

  const { shortages, blocking } = dialog;
  const name = (s: StockShortageDto): string =>
    (lang === 'ar' ? s.name_ar : s.name) || s.name || s.name_ar || s.code;
  const unit = (s: StockShortageDto): string =>
    (lang === 'ar' ? s.unit_name_ar : s.unit_name) || s.unit_name || '';

  return (
    <div className="modal-overlay show stock-shortage-overlay" onClick={closeDialog}>
      <div
        className={`modal stock-shortage-modal${blocking ? ' is-blocking' : ' is-warning'}`}
        role="alertdialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="modal-header-img stock-shortage-icon" aria-hidden="true">
            {blocking ? '⛔' : '⚠️'}
          </div>
          <div className="modal-header-title">
            <div className="modal-header-name">
              {blocking
                ? t({ en: 'Not enough stock', ar: 'الرصيد غير كافٍ' })
                : t({ en: 'Stock will go negative', ar: 'سيصبح الرصيد بالسالب' })}
            </div>
            <div className="modal-header-name-ar">
              {blocking
                ? t({
                    en: 'This company does not allow selling below the available quantity. Reduce the quantities marked below or remove the items to continue.',
                    ar: 'لا تسمح هذه الشركة بالبيع بأكثر من الكمية المتاحة. قلّل الكميات المحددة أدناه أو احذف الأصناف للمتابعة.',
                  })
                : t({
                    en: 'The quantities below exceed what is on hand. You can still complete the sale.',
                    ar: 'الكميات أدناه تتجاوز الرصيد المتاح. لا يزال بإمكانك إتمام البيع.',
                  })}
            </div>
          </div>
          <button type="button" className="modal-close" onClick={closeDialog} aria-label={t({ en: 'Close', ar: 'إغلاق' })}>
            <XIcon size={18} />
          </button>
        </div>

        <div className="modal-body">
          <table className="stock-shortage-table">
            <thead>
              <tr>
                <th>{t({ en: 'Item', ar: 'الصنف' })}</th>
                <th className="num">{t({ en: 'Requested', ar: 'المطلوب' })}</th>
                <th className="num">{t({ en: 'Available', ar: 'المتاح' })}</th>
                <th className="num">{t({ en: 'Short by', ar: 'النقص' })}</th>
              </tr>
            </thead>
            <tbody>
              {shortages.map((s) => (
                <tr key={`${s.item_id}-${s.via_kit_item_id ?? 0}`}>
                  <td>
                    <div className="stock-shortage-name">{name(s)}</div>
                    <div className="stock-shortage-meta">
                      {t({ en: 'Code', ar: 'كود' })} {s.code}
                      {s.via_kit_item_id != null && (
                        <> · {t({ en: 'part of a package item', ar: 'ضمن صنف مجمّع' })}</>
                      )}
                    </div>
                  </td>
                  <td className="num">{fmtQty(s.requested)} {unit(s)}</td>
                  <td className={`num${s.available <= 0 ? ' is-zero' : ''}`}>{fmtQty(s.available)} {unit(s)}</td>
                  <td className="num short">{fmtQty(s.requested - s.available)} {unit(s)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="modal-footer stock-shortage-footer">
          <button type="button" className="btn-primary-modal" onClick={closeDialog}>
            {blocking ? t({ en: 'Back to the cart', ar: 'العودة إلى السلة' }) : t({ en: 'OK', ar: 'حسناً' })}
          </button>
        </div>
      </div>
    </div>
  );
}

function fmtQty(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 3 });
}
